export const applicationEndpoint = () =>
  process.env.NEXT_PUBLIC_APPLY_FUNCTION_URL ||
  'https://europe-west1-datacode-327ec.cloudfunctions.net/sendApplyEmail';

export class SubmissionError extends Error {
  constructor(
    message: string,
    public field?: string,
  ) {
    super(message);
  }
}

/**
 * User-facing error text. English is the default so existing callers behave
 * exactly as before. Translated locales pass their own copy; `localized`
 * makes the generic message win over the (English) text the server sends,
 * while the `field` from the server is still honoured.
 */
export type SubmissionMessages = {
  readCv: string;
  saveFailed: string;
  unconfirmed: string;
  interrupted: string;
  localized?: boolean;
};

const ENGLISH_MESSAGES: SubmissionMessages = {
  readCv: 'We could not read your CV. Please select it again.',
  saveFailed: 'We could not save your application. Please try again.',
  unconfirmed:
    'We could not confirm your application. Please retry; this will not create a second application.',
  interrupted:
    'Your connection was interrupted. Your answers are still here. Retry to confirm your application.',
};

export async function readCV(
  file: File,
  messages: SubmissionMessages = ENGLISH_MESSAGES,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(new Error(messages.readCv));
    reader.readAsDataURL(file);
  });
}

export function submitApplication(
  payload: Record<string, unknown>,
  onProgress: (percent: number) => void,
  messages: SubmissionMessages = ENGLISH_MESSAGES,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', applicationEndpoint());
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.timeout = 120000;
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable)
        onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      try {
        const body = JSON.parse(xhr.responseText);
        if (
          xhr.status >= 200 &&
          xhr.status < 300 &&
          body.success === true &&
          typeof body.applicationId === 'string'
        )
          resolve(body.applicationId);
        else
          reject(
            new SubmissionError(
              (!messages.localized && body.error) || messages.saveFailed,
              body.field,
            ),
          );
      } catch {
        reject(new Error(messages.unconfirmed));
      }
    };
    xhr.onerror = xhr.ontimeout = () =>
      reject(new Error(messages.interrupted));
    xhr.send(JSON.stringify(payload));
  });
}

export function newApplicationIdentity() {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return {
    applicationId: crypto.randomUUID(),
    submissionToken: Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, '0'),
    ).join(''),
  };
}
